// Wrapper component that adds pill-style sub-tab navigation above the Product Mapping content area.
// Sub-tabs: By-Product Mapping (existing), Input/Output Mapping (placeholder), Packing Material Mapping (placeholder).

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package, ArrowRightLeft, BoxSelect } from 'lucide-react';
import ByproductMappingPage from './ByproductMapping';
import PackingMaterialMappingPage from './PackingMaterialMapping';
import InputOutputMapping from './InputOutputMapping';
import { useLanguage } from '../../i18n/LanguageContext';

type SubTab = 'byproduct' | 'inputOutput' | 'packingMaterial';

const ProductMappingTabs: React.FC = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeSubTab = (searchParams.get('subtab') as SubTab) || 'byproduct';

  const setSubTab = (subtab: SubTab) => {
    const params: Record<string, string> = { tab: 'productMapping' };
    if (subtab !== 'byproduct') params.subtab = subtab;
    setSearchParams(params, { replace: true });
  };

  const subTabs: { id: SubTab; label: string; icon: React.ElementType }[] = [
    { id: 'byproduct', label: t('byproductMapping.byproductTab'), icon: Package },
    { id: 'inputOutput', label: t('byproductMapping.inputOutputTab'), icon: ArrowRightLeft },
    { id: 'packingMaterial', label: t('byproductMapping.packingMaterialTab'), icon: BoxSelect },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Sub-tab navigation pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {subTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setSubTab(id)}
            className={`
              inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors
              ${activeSubTab === id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-secondary-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }
            `}
          >
            <Icon className="w-4 h-4 mr-2" />
            {label}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      <div className="flex-1 min-h-0">
        {activeSubTab === 'byproduct' && <ByproductMappingPage />}

        {activeSubTab === 'inputOutput' && <InputOutputMapping />}

        {activeSubTab === 'packingMaterial' && <PackingMaterialMappingPage />}
      </div>
    </div>
  );
};

export default ProductMappingTabs;
